import { useContext, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";

import createWalletTopupMutation from "../api/postWalletTopup.ts";
import FormRow from "../../../../shared/input/FormRow.tsx";
import TextInput from "../../../../shared/input/Text.tsx";
import Button from "../../../../shared/button/Button.tsx";
import { GlobalOverlayContext } from "../../../../context/OverlayContext.tsx";
import createCompanyWalletQuery from "../api/getCompanyWallet.ts";
import { WalletData } from "../lib/types.ts";

const TopupForm = ({ slug }) => {
  const { closeModal } = useContext(GlobalOverlayContext);
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    tokens_added: 1000,
    validity_days: 30,
    flat_fee: 4999,
    name: "Custom",
  });
  const { refetch } = useQuery<WalletData>(createCompanyWalletQuery(slug));

  const { mutate, isPending } = useMutation(createWalletTopupMutation(slug));

  const handleNumberChange = (
    key: "tokens_added" | "validity_days" | "flat_fee",
    value: string
  ) => {
    setFormData((prev) => ({ ...prev, [key]: Number(value) }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      tokens_added: formData.tokens_added,
      validity_days: formData.validity_days,
      flat_fee: formData.flat_fee,
      ...(formData.name.trim() ? { name: formData.name.trim() } : {}),
    };
    mutate(payload, {
      onSuccess: () => {
        refetch();
        queryClient.invalidateQueries({ queryKey: ["sourcing-access", slug] });
        queryClient.invalidateQueries({ queryKey: ["sourcing-payment-history", slug] });
        toast.success("Wallet topped up successfully");
        closeModal();
      },
      onError: () => {
        toast.error("Failed to top up wallet. Please try again.");
        closeModal();
      },
    });
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <FormRow
        label={{ text: "Plan Name", variant: "primary" }}
        className={{ wrapper: "gap-1.5" }}
      >
        <TextInput
          type="text"
          value={formData.name}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, name: e.target.value }))
          }
          placeholder="Optional"
        />
      </FormRow>
      <FormRow
        label={{ text: "Credit added", variant: "primary", required: true }}
        className={{ wrapper: "gap-1.5" }}
      >
        <TextInput
          type="number"
          value={formData.tokens_added}
          onChange={(e) => handleNumberChange("tokens_added", e.target.value)}
          min={1}
          required
        />
      </FormRow>
      <FormRow
        label={{ text: "Credit validity (days)", variant: "primary", required: true }}
        className={{ wrapper: "gap-1.5" }}
      >
        <TextInput
          type="number"
          value={formData.validity_days}
          onChange={(e) => handleNumberChange("validity_days", e.target.value)}
          min={1}
          required
        />
      </FormRow>
      <FormRow
        label={{ text: "Flat Fee (₹)", variant: "primary", required: true }}
        className={{ wrapper: "gap-1.5" }}
      >
        <TextInput
          type="number"
          value={formData.flat_fee}
          onChange={(e) => handleNumberChange("flat_fee", e.target.value)}
          min={0}
          required
        />
      </FormRow>
      <div className="flex justify-end pt-2">
        <Button type="submit" variant="primary" size="sm" disabled={isPending}>
          {isPending ? "Processing..." : "Confirm Topup"}
        </Button>
      </div>
    </form>
  );
};

export default TopupForm;
